import { apiFetch } from '@/lib/api';
import type { MediaItem } from '@/types/blog';

export const mediaService = {
  async list(filters?: {
    search?: string;
    mime_type?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ data: MediaItem[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.search) params.set('search', filters.search);
    if (filters?.mime_type) params.set('mime_type', filters.mime_type);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));
    const res = await apiFetch(`/api/admin/media?${params}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to list media');
    return json;
  },

  async presign(filename: string, contentType: string): Promise<{ uploadUrl: string; r2Key: string; publicUrl: string }> {
    const res = await apiFetch('/api/admin/media/presign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename, contentType }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to get upload URL');
    return json.data;
  },

  async finalize(data: {
    filename: string;
    original_name: string;
    mime_type: string;
    file_size?: number;
    r2_key: string;
    url: string;
    alt_text?: string;
    width?: number;
    height?: number;
  }): Promise<MediaItem> {
    const res = await apiFetch('/api/admin/media/finalize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to finalize upload');
    return json.data;
  },

  async update(id: string, data: { alt_text?: string | null; caption?: string | null }): Promise<MediaItem> {
    const res = await apiFetch(`/api/admin/media/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to update media');
    return json.data;
  },

  async delete(id: string): Promise<void> {
    const res = await apiFetch(`/api/admin/media/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const json = await res.json();
      throw new Error(json.error || 'Failed to delete media');
    }
  },

  async bulkDelete(ids: string[]): Promise<void> {
    const res = await apiFetch('/api/admin/media/bulk-delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) {
      const json = await res.json();
      throw new Error(json.error || 'Failed to delete media items');
    }
  },

  async uploadFile(file: File): Promise<MediaItem> {
    // 1. Get presigned URL
    const { uploadUrl, r2Key, publicUrl } = await mediaService.presign(file.name, file.type);

    // 2. Upload to R2
    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });

    // 3. Get image dimensions if applicable
    let width: number | undefined;
    let height: number | undefined;
    if (file.type.startsWith('image/')) {
      const dims = await getImageDimensions(file);
      width = dims.width;
      height = dims.height;
    }

    // 4. Finalize
    return mediaService.finalize({
      filename: r2Key.split('/').pop() || file.name,
      original_name: file.name,
      mime_type: file.type,
      file_size: file.size,
      r2_key: r2Key,
      url: publicUrl,
      width,
      height,
    });
  },
};

function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });
}
